<template>
  <!-- Job 管理：维护接口请求、断言策略，并可单独执行一个 Job。 -->
  <section class="view jobs-view">
    <div class="split-grid">
      <section class="surface">
        <div class="surface-head">
          <div>
            <h2>Job 列表</h2>
            <span>{{ jobs.length }} 条记录</span>
          </div>
          <div class="surface-actions">
            <el-button :icon="icons.Collection" @click="openTemplateDialog">
              模板库
            </el-button>
            <el-button
              v-if="jobForm.id"
              type="primary"
              plain
              :icon="icons.Share"
              :loading="sharingJobTemplateId === Number(jobForm.id)"
              @click="handleShareJob(Number(jobForm.id))"
            >
              分享模板
            </el-button>
          </div>
        </div>

        <el-table
          :data="jobs"
          row-key="id"
          class="job-table"
          height="540"
          highlight-current-row
          :row-class-name="jobRowClassName"
          @row-click="handleJobRowClick"
        >
          <el-table-column label="Step" min-width="220">
            <template #default="{ row }">
              <div class="job-name">
                <strong>{{ row.step || '-' }}</strong>
                <span v-if="row.step_desc">{{ row.step_desc }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="方式" width="92">
            <template #default="{ row }">
              <el-tag :type="methodTagType(row.http_type)" effect="light">
                {{ row.http_type || 'GET' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="规则" width="82" align="center">
            <template #default="{ row }">{{ row.rule_count || 0 }}</template>
          </el-table-column>
          <el-table-column label="绑定规则" width="132" align="center">
            <template #default="{ row }">
              <el-button
                size="small"
                :type="jobRunIncludesRules(row.id) ? 'primary' : 'info'"
                :plain="!jobRunIncludesRules(row.id)"
                @click.stop="toggleJobRunRules(row.id)"
              >
                {{ jobRunIncludesRules(row.id) ? '规则开启' : '不带规则' }}
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="断言" width="152">
            <template #default="{ row }">
              <el-tag v-if="assertionLabel(row)" :type="assertionTagType(row)" effect="plain">
                {{ assertionLabel(row) }}
              </el-tag>
              <span v-else class="muted">未执行</span>
            </template>
          </el-table-column>
          <el-table-column label="URL" min-width="260" show-overflow-tooltip>
            <template #default="{ row }">{{ row.url || '-' }}</template>
          </el-table-column>
          <el-table-column label="更新时间" width="168">
            <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="334" fixed="right">
            <template #default="{ row }">
              <el-button
                size="small"
                :icon="icons.CopyDocument"
                @click.stop="copyJob(row.id)"
              >
                复制
              </el-button>
              <el-button
                size="small"
                :icon="icons.Share"
                :loading="sharingJobTemplateId === Number(row.id)"
                @click.stop="handleShareJob(row.id)"
              >
                分享
              </el-button>
              <el-button
                size="small"
                :icon="icons.VideoPlay"
                :loading="runningJobId === Number(row.id)"
                @click.stop="runJob({ id: row.id, includeRules: jobRunIncludesRules(row.id) })"
              >
                执行
              </el-button>
              <el-button
                size="small"
                :icon="icons.Odometer"
                :loading="runningJobStressId === Number(row.id)"
                @click.stop="openJobStressDialog(row)"
              >
                压测
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section class="surface editor-surface">
        <div class="surface-head">
          <div>
            <h2>{{ jobForm.id ? `编辑 TestJob #${jobForm.id}` : '新增 TestJob' }}</h2>
            <span>{{ jobForm.id ? '已选择 Job' : '新建配置' }}</span>
          </div>
          <div class="surface-actions">
            <el-button
              v-if="!jobForm.id"
              :icon="icons.LinkIcon"
              @click="openCurlDialog"
            >
              解析 cURL
            </el-button>
            <el-button
              v-if="jobForm.id"
              type="danger"
              plain
              :icon="icons.Delete"
              @click="deleteJob"
            >
              删除
            </el-button>
          </div>
        </div>

        <el-form
          ref="jobEditor"
          :model="jobForm"
          :rules="jobFormRules"
          label-position="top"
          class="job-form"
          @submit.prevent
        >
          <el-row :gutter="14">
            <el-col :xs="24" :md="12">
              <el-form-item label="Step" prop="step">
                <div class="step-input-row">
                  <el-input
                    :model-value="jobForm.step"
                    maxlength="255"
                    show-word-limit
                    @update:model-value="updateJobField('step', $event)"
                  />
                  <el-button :icon="icons.Refresh" @click="generateRandomStep">随机</el-button>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item label="步骤描述">
                <el-input
                  :model-value="jobForm.step_desc"
                  maxlength="255"
                  show-word-limit
                  @update:model-value="updateJobField('step_desc', $event)"
                />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="8">
              <el-form-item label="请求方式">
                <el-select
                  :model-value="jobForm.http_type"
                  @update:model-value="updateJobField('http_type', $event)"
                >
                  <el-option v-for="method in httpTypes" :key="method" :label="method" :value="method" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="16">
              <el-form-item label="URL" prop="url">
                <el-input
                  :model-value="jobForm.url"
                  maxlength="255"
                  placeholder="https://example.com/api"
                  @update:model-value="updateJobField('url', $event)"
                >
                  <template #prefix>
                    <el-icon><LinkIcon /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="Return Rule">
                <el-input
                  :model-value="jobForm.return_rule"
                  type="textarea"
                  :autosize="{ minRows: 2, maxRows: 4 }"
                  maxlength="255"
                  @update:model-value="updateJobField('return_rule', $event)"
                />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="断言失败处理">
                <el-radio-group
                  :model-value="jobForm.return_rule_end"
                  @update:model-value="updateJobField('return_rule_end', Number($event))"
                >
                  <el-radio-button :label="1">阻塞流程</el-radio-button>
                  <el-radio-button :label="0">不阻塞流程</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="差异请求头 JSON">
                <div class="json-field-tools">
                  <div v-if="jobForm.id" class="session-fill-control">
                    <el-input
                      v-model="sessionPhone"
                      clearable
                      placeholder="手机号"
                      inputmode="tel"
                    />
                    <el-button
                      :icon="icons.Key"
                      :loading="fillingSession"
                      @click="handleFillSessionHeaders"
                    >
                      填充 session/userId
                    </el-button>
                  </div>
                  <el-button
                    :icon="icons.Tickets"
                    :loading="fillingIin"
                    @click="handleFillIin"
                  >
                    填充 iin
                  </el-button>
                </div>
                <el-input
                  :model-value="jobForm.requestHeaders"
                  class="code-input"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 8 }"
                  spellcheck="false"
                  @update:model-value="updateJobField('requestHeaders', $event)"
                />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="请求参数 JSON">
                <el-input
                  :model-value="jobForm.requestParams"
                  class="code-input"
                  type="textarea"
                  :autosize="{ minRows: 4, maxRows: 8 }"
                  spellcheck="false"
                  @update:model-value="updateJobField('requestParams', $event)"
                />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item label="请求体 JSON">
                <el-input
                  :model-value="jobForm.requestBody"
                  class="code-input"
                  type="textarea"
                  :autosize="{ minRows: 5, maxRows: 10 }"
                  spellcheck="false"
                  @update:model-value="updateJobField('requestBody', $event)"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <div class="form-actions">
            <el-button type="primary" :icon="icons.Select" @click="handleSaveJob">
              保存 TestJob
            </el-button>
            <el-button :icon="icons.Plus" @click="handleResetJobForm">清空</el-button>
            <el-button
              v-if="jobForm.id"
              :icon="icons.CopyDocument"
              @click="copyJob(Number(jobForm.id))"
            >
              复制 Job
            </el-button>
            <el-button
              v-if="jobForm.id"
              :icon="icons.Share"
              :loading="sharingJobTemplateId === Number(jobForm.id)"
              @click="handleShareJob(Number(jobForm.id))"
            >
              分享模板
            </el-button>
            <el-button
              v-if="jobForm.id"
              :type="jobRunIncludesRules(jobForm.id) ? 'primary' : 'info'"
              :plain="!jobRunIncludesRules(jobForm.id)"
              @click="toggleJobRunRules(jobForm.id)"
            >
              {{ jobRunIncludesRules(jobForm.id) ? '规则开启' : '不带规则' }}
            </el-button>
            <el-button
              v-if="jobForm.id"
              :icon="icons.VideoPlay"
              :loading="runningJobId === Number(jobForm.id)"
              @click="runJob({ id: Number(jobForm.id), includeRules: jobRunIncludesRules(jobForm.id) })"
            >
              执行 Job
            </el-button>
            <el-button
              v-if="jobForm.id"
              :icon="icons.Odometer"
              :loading="runningJobStressId === Number(jobForm.id)"
              @click="openJobStressDialog(jobForm)"
            >
              压力测试
            </el-button>
          </div>
        </el-form>
      </section>
    </div>

    <section class="surface">
      <div class="surface-head">
        <div>
          <h2>执行结果</h2>
          <span v-if="selectedJob">Job #{{ selectedJob.id }}</span>
          <span v-else>暂无选择</span>
        </div>
        <div v-if="currentAssertion" class="assertion-actions">
          <el-tag :type="currentAssertion.ok ? 'success' : 'danger'">
            {{ currentAssertion.ok ? '断言通过' : '断言失败' }}
          </el-tag>
          <el-button
            v-if="currentAssertionFailures.length"
            size="small"
            type="danger"
            plain
            @click="openAssertionDialog"
          >
            查看原因
          </el-button>
        </div>
      </div>
      <div class="result-grid">
        <section class="result-panel">
          <h3>请求参数</h3>
          <pre class="response-box">{{ currentRequestText || '暂无请求参数' }}</pre>
        </section>
        <section class="result-panel">
          <h3>响应体</h3>
          <pre class="response-box">{{ currentResponseBodyText || '暂无响应体' }}</pre>
        </section>
      </div>
      <section v-if="currentAssertionFailures.length" class="assertion-failures">
        <h3>断言失败明细</h3>
        <ul class="assertion-failure-list">
          <li v-for="reason in currentAssertionFailures" :key="reason">{{ reason }}</li>
        </ul>
      </section>
    </section>

    <el-dialog
      v-model="assertionDialogVisible"
      title="断言失败原因"
      width="640px"
      class="assertion-dialog"
    >
      <ul class="assertion-dialog-list">
        <li v-for="reason in currentAssertionFailures" :key="reason">{{ reason }}</li>
      </ul>
      <template #footer>
        <el-button @click="assertionDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="stressDialogVisible"
      title="Job 压力测试"
      width="760px"
      class="stress-dialog"
    >
      <section class="stress-target">
        <strong>{{ stressJobLabel }}</strong>
        <span>{{ stressTargetJob && stressTargetJob.url ? stressTargetJob.url : '-' }}</span>
      </section>

      <el-form label-position="top" class="stress-form">
        <div class="stress-form-grid">
          <el-form-item label="线程数">
            <el-input-number v-model="stressForm.threads" :min="1" :max="20" :step="1" />
          </el-form-item>
          <el-form-item label="轮次数量">
            <el-input-number v-model="stressForm.rounds" :min="1" :max="stressMaxRounds" :step="1" />
          </el-form-item>
          <el-form-item label="Rule">
            <el-switch
              v-model="stressForm.includeRules"
              active-text="包含 Rule"
              inactive-text="不带 Rule"
            />
          </el-form-item>
        </div>
      </el-form>

      <section v-if="stressTask" class="stress-progress">
        <div class="stress-progress-head">
          <span>{{ stressTaskStatusText }}</span>
          <strong>{{ stressTaskCompleted }} / {{ stressTaskTotal }}</strong>
        </div>
        <el-progress :percentage="stressProgressPercent" :status="stressProgressStatus" />
      </section>

      <section v-if="stressResult" class="stress-result">
        <div class="stress-metric-grid">
          <div class="stress-metric">
            <span>成功率</span>
            <strong>{{ stressResult.success_rate }}%</strong>
          </div>
          <div class="stress-metric">
            <span>成功 / 失败</span>
            <strong>{{ stressResult.success_count }} / {{ stressResult.failed_count }}</strong>
          </div>
          <div class="stress-metric">
            <span>QPS</span>
            <strong>{{ stressResult.throughput_per_second }}</strong>
          </div>
          <div class="stress-metric">
            <span>平均耗时</span>
            <strong>{{ formatStressMs(stressResult.avg_ms) }}</strong>
          </div>
          <div class="stress-metric">
            <span>P95</span>
            <strong>{{ formatStressMs(stressResult.p95_ms) }}</strong>
          </div>
          <div class="stress-metric">
            <span>总耗时</span>
            <strong>{{ formatStressMs(stressResult.total_duration_ms) }}</strong>
          </div>
        </div>
        <ul v-if="(stressResult.failed_samples || []).length" class="stress-failure-list">
          <li v-for="sample in stressResult.failed_samples || []" :key="sample.index">
            #{{ sample.index }} {{ formatStressMs(sample.duration_ms) }}：{{ sample.failure }}
          </li>
        </ul>
      </section>

      <template #footer>
        <el-button @click="stressDialogVisible = false">关闭</el-button>
        <el-button
          type="primary"
          :loading="Boolean(stressTargetJob && runningJobStressId === Number(stressTargetJob.id))"
          :disabled="stressRunning"
          @click="handleRunJobStress"
        >
          {{ stressRunning ? "压测中" : "开始压测" }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="curlDialogVisible"
      title="从 cURL 解析 Job"
      width="720px"
    >
      <el-input
        v-model="curlText"
        class="code-input"
        type="textarea"
        :autosize="{ minRows: 8, maxRows: 14 }"
        spellcheck="false"
        placeholder="curl 'https://example.com/api' -H 'Content-Type: application/json' --data-raw '{...}'"
      />
      <template #footer>
        <el-button @click="curlDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="parsingCurl" @click="handleParseCurl">
          解析并填充
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="templateDialogVisible"
      title="Job 模板库"
      width="920px"
      class="template-dialog"
      @open="loadJobTemplates"
    >
      <el-table
        :data="jobTemplates"
        row-key="id"
        height="420"
        class="template-table"
        empty-text="暂无公开模板"
      >
        <el-table-column label="模板" min-width="220">
          <template #default="{ row }">
            <div class="job-name">
              <strong>{{ row.step || '-' }}</strong>
              <span>{{ row.step_desc || '未填写描述' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="方式" width="88">
          <template #default="{ row }">
            <el-tag :type="methodTagType(row.http_type)" effect="light">
              {{ row.http_type || 'GET' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="分享人" width="132">
          <template #default="{ row }">
            <div class="template-owner">
              <span>{{ row.owner_username || '-' }}</span>
              <el-tag v-if="row.shared_by_me" size="small" effect="plain">我</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="规则" width="74" align="center">
          <template #default="{ row }">{{ row.rule_count || 0 }}</template>
        </el-table-column>
        <el-table-column label="URL" min-width="240" show-overflow-tooltip>
          <template #default="{ row }">{{ row.url || '-' }}</template>
        </el-table-column>
        <el-table-column label="更新时间" width="168">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="132" fixed="right">
          <template #default="{ row }">
            <el-button
              type="primary"
              size="small"
              :icon="icons.Download"
              :loading="copyingJobTemplateId === Number(row.id)"
              @click="handleCopyTemplate(row.id)"
            >
              导入
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="templateDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import {
  Collection,
  CopyDocument,
  Delete,
  Download,
  Key,
  Link as LinkIcon,
  Odometer,
  Plus,
  Refresh,
  Select,
  Share,
  Tickets,
  VideoPlay,
} from "@element-plus/icons-vue";
import { assertionLabel, assertionTagType, formatDateTime, methodTagType } from "../utils/job-display";
import {
  assertionFailureReasons,
  formatRequestParams,
  formatResponseBody,
} from "../utils/result-format";

export default {
  name: "JobsPage",

  components: {
    LinkIcon,
  },

  data() {
    return {
      icons: {
        Collection,
        CopyDocument,
        Delete,
        Download,
        Key,
        LinkIcon,
        Odometer,
        Plus,
        Refresh,
        Select,
        Share,
        Tickets,
        VideoPlay,
      },
      assertionDialogVisible: false,
      curlDialogVisible: false,
      stressDialogVisible: false,
      templateDialogVisible: false,
      curlText: "",
      fillingIin: false,
      fillingSession: false,
      parsingCurl: false,
      sessionPhone: "",
      stressTargetJob: null,
      stressTask: null,
      stressPollTimer: null,
      stressResult: null,
      stressForm: {
        threads: 1,
        rounds: 10,
        includeRules: true,
      },
      jobFormRules: {
        step: [{ required: true, message: "请输入 Step", trigger: "blur" }],
        url: [{ required: true, message: "请输入 URL", trigger: "blur" }],
      },
    };
  },

  computed: {
    ...mapState([
      "httpTypes",
      "jobs",
      "jobTemplates",
      "selectedJob",
      "jobRunRuleSelections",
      "runningJobId",
      "runningJobStressId",
      "sharingJobTemplateId",
      "copyingJobTemplateId",
      "jobForm",
    ]),
    ...mapGetters(["currentResult", "currentAssertion"]),

    currentRequestText() {
      return formatRequestParams(this.currentResult);
    },

    currentResponseBodyText() {
      return formatResponseBody(this.currentResult);
    },

    currentAssertionFailures() {
      return assertionFailureReasons(this.currentAssertion);
    },

    stressJobLabel() {
      if (!this.stressTargetJob) {
        return "未选择 Job";
      }
      const step = this.stressTargetJob.step || "-";
      const description = this.stressTargetJob.step_desc || "未填写描述";
      return `Step ${step} - ${description}`;
    },

    stressTotalRuns() {
      return Number(this.stressForm.threads || 0) * Number(this.stressForm.rounds || 0);
    },

    stressMaxRounds() {
      const threads = Math.min(20, Math.max(1, Number(this.stressForm.threads || 1)));
      return Math.max(1, Math.floor(200 / threads));
    },

    stressRunning() {
      return Boolean(this.stressTask && this.stressTask.status === "running");
    },

    stressTaskCompleted() {
      return this.stressTask ? Number(this.stressTask.completed || 0) : 0;
    },

    stressTaskTotal() {
      return this.stressTask ? Number(this.stressTask.total || 0) : this.stressTotalRuns;
    },

    stressProgressPercent() {
      if (!this.stressTaskTotal) {
        return 0;
      }
      return Math.min(100, Math.round((this.stressTaskCompleted / this.stressTaskTotal) * 100));
    },

    stressProgressStatus() {
      if (!this.stressTask) {
        return "";
      }
      if (this.stressTask.status === "finished") {
        return "success";
      }
      if (this.stressTask.status === "failed") {
        return "exception";
      }
      return "";
    },

    stressTaskStatusText() {
      if (!this.stressTask) {
        return "";
      }
      if (this.stressTask.status === "finished") {
        return "压测完成";
      }
      if (this.stressTask.status === "failed") {
        return "压测失败";
      }
      return `压测中：${this.stressTask.options?.threads || this.stressForm.threads} 线程 × ${this.stressTask.options?.rounds || this.stressForm.rounds} 轮`;
    },
  },

  beforeUnmount() {
    this.clearStressPoll();
    if (this.stressRunning) {
      this.setRunningJobStressId(null);
    }
  },

  methods: {
    ...mapActions([
      "selectJob",
      "saveJob",
      "deleteJob",
      "runJob",
      "parseCurlJob",
      "copyJob",
      "stressJob",
      "fetchStressTask",
      "loadJobTemplates",
      "shareJobTemplate",
      "copyJobTemplate",
      "fillJobSessionHeaders",
      "fillJobIin",
    ]),
    ...mapMutations({
      resetJobFormState: "resetJobForm",
      setJobFormFieldMutation: "setJobFormField",
      setJobRunWithRules: "setJobRunWithRules",
      setRunningJobStressId: "setRunningJobStressId",
    }),
    assertionLabel,
    assertionTagType,
    formatDateTime,
    methodTagType,

    updateJobField(field, value) {
      this.setJobFormFieldMutation({ field, value });
    },

    jobRunIncludesRules(jobId) {
      const value = this.jobRunRuleSelections[String(jobId)];
      return value === undefined ? true : value;
    },

    toggleJobRunRules(jobId) {
      this.setJobRunWithRules({
        id: jobId,
        value: !this.jobRunIncludesRules(jobId),
      });
    },

    generateRandomStep() {
      const suffix = Math.floor(Math.random() * 900) + 100;
      this.updateJobField("step", String(Date.now() * 1000 + suffix));
    },

    openCurlDialog() {
      this.curlText = "";
      this.curlDialogVisible = true;
    },

    openTemplateDialog() {
      this.templateDialogVisible = true;
      this.loadJobTemplates();
    },

    async handleParseCurl() {
      this.parsingCurl = true;
      const parsed = await this.parseCurlJob(this.curlText);
      this.parsingCurl = false;
      if (!parsed) {
        return;
      }
      this.curlDialogVisible = false;
      this.$nextTick(() => {
        this.$refs.jobEditor?.clearValidate();
      });
    },

    async handleSaveJob() {
      try {
        await this.$refs.jobEditor.validate();
      } catch (error) {
        return;
      }
      await this.saveJob();
    },

    async handleFillSessionHeaders() {
      if (!this.jobForm.id) {
        return;
      }
      this.fillingSession = true;
      try {
        await this.fillJobSessionHeaders({
          jobId: Number(this.jobForm.id),
          phone: this.sessionPhone,
        });
      } finally {
        this.fillingSession = false;
      }
    },

    async handleFillIin() {
      this.fillingIin = true;
      try {
        await this.fillJobIin();
      } finally {
        this.fillingIin = false;
      }
    },

    async handleShareJob(id) {
      await this.shareJobTemplate(id);
    },

    openJobStressDialog(job) {
      if (!job || !job.id) {
        this.$store.dispatch("showToast", { message: "请选择要压测的 Job", isError: true });
        return;
      }
      this.stressTargetJob = job;
      this.stressResult = null;
      this.stressTask = null;
      this.stressForm.includeRules = this.jobRunIncludesRules(job.id);
      this.stressDialogVisible = true;
    },

    async handleRunJobStress() {
      if (!this.stressTargetJob || !this.stressTargetJob.id) {
        return;
      }
      this.normalizeStressForm();
      this.clearStressPoll();
      this.stressResult = null;
      this.stressTask = null;
      const task = await this.stressJob({
        id: Number(this.stressTargetJob.id),
        threads: this.stressForm.threads,
        rounds: this.stressForm.rounds,
        includeRules: this.stressForm.includeRules,
      });
      if (task) {
        await this.applyJobStressTask(task);
      }
    },

    normalizeStressForm() {
      const threads = Math.min(20, Math.max(1, Number(this.stressForm.threads || 1)));
      const maxRounds = Math.max(1, Math.floor(200 / threads));
      const rounds = Math.min(maxRounds, Math.max(1, Number(this.stressForm.rounds || 1)));
      this.stressForm.threads = threads;
      this.stressForm.rounds = rounds;
    },

    clearStressPoll() {
      if (this.stressPollTimer) {
        window.clearTimeout(this.stressPollTimer);
        this.stressPollTimer = null;
      }
    },

    scheduleJobStressPoll(taskId) {
      this.clearStressPoll();
      this.stressPollTimer = window.setTimeout(() => {
        this.pollJobStressTask(taskId);
      }, 800);
    },

    async pollJobStressTask(taskId) {
      const task = await this.fetchStressTask(taskId);
      if (!task) {
        this.clearStressPoll();
        this.setRunningJobStressId(null);
        return;
      }
      await this.applyJobStressTask(task);
    },

    async applyJobStressTask(task) {
      this.stressTask = task;
      if (task.status === "running") {
        this.scheduleJobStressPoll(task.task_id);
        return;
      }

      this.clearStressPoll();
      this.setRunningJobStressId(null);
      if (task.status === "finished" && task.result) {
        this.stressResult = task.result;
        this.$store.dispatch("showToast", {
          message: `Job 压测完成，成功率 ${task.result.success_rate}%`,
        });
        await this.$store.dispatch("loadJobs");
        return;
      }

      this.$store.dispatch("showToast", {
        message: task.error || "Job 压测失败",
        isError: true,
      });
    },

    formatStressMs(value) {
      return `${Number(value || 0).toFixed(2)} ms`;
    },

    async handleCopyTemplate(id) {
      const copied = await this.copyJobTemplate(id);
      if (copied) {
        this.templateDialogVisible = false;
      }
    },

    handleResetJobForm() {
      this.resetJobFormState();
      this.$nextTick(() => {
        this.$refs.jobEditor?.clearValidate();
      });
    },

    handleJobRowClick(row) {
      this.selectJob(row.id);
    },

    jobRowClassName({ row }) {
      return this.selectedJob && this.selectedJob.id === row.id ? "selected-row" : "";
    },

    openAssertionDialog() {
      if (this.currentAssertionFailures.length) {
        this.assertionDialogVisible = true;
      }
    },
  },
};
</script>
